import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL!;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY!;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('環境変数が設定されていません。');
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
  },
});

export default supabase;



// セッション変更の監視
supabase.auth.onAuthStateChange((event, session) => {
  console.log('Auth state changed:', event, session?.user?.email);
  
  // Handle password recovery redirect
  if (event === 'PASSWORD_RECOVERY') {
    console.log('Password recovery event detected');
    
    // Store the current URL for recovery
    try {
      localStorage.setItem('auth_recovery_url', window.location.href);
      localStorage.setItem('auth_recovery_event', 'PASSWORD_RECOVERY');
      localStorage.setItem('auth_recovery_time', new Date().toISOString());
      
      // Try to extract and store token
      const token = extractAccessTokenFromUrl();
      if (token) {
        localStorage.setItem('auth_access_token', token);
        localStorage.setItem('auth_refresh_token', token); // Also store as refresh token for compatibility
        console.log('Stored access token from auth state change');
      }
    } catch (e) {
      console.error('Failed to store auth recovery URL:', e);
    }
    
    // Redirect to reset password page if not already there
    if (!window.location.pathname.includes('/reset-password')) {
      console.log('Redirecting to reset password page');
      
      // 直接リダイレクト
      window.location.href = '/reset-password?type=recovery';
    }
  }
});

// Add a function to manually handle password reset redirects
export const handlePasswordResetRedirect = () => {
  const fullUrl = window.location.href;
  
  // Check for access_token in search params or type=recovery in search params
  const hasAccessToken = fullUrl.includes('access_token=');
  const hasTypeRecovery = fullUrl.includes('type=recovery');
  const hasTypeResetPassword = fullUrl.includes('type=reset_password');
  
  if (hasAccessToken || hasTypeRecovery || hasTypeResetPassword) {
    console.log('Password reset URL detected in handlePasswordResetRedirect');
    
    // Store the URL for recovery
    try {
      localStorage.setItem('auth_recovery_url', fullUrl);
      localStorage.setItem('auth_recovery_time', new Date().toISOString());
      
      // Try to extract and store access token
      const accessToken = extractAccessTokenFromUrl();
      if (accessToken) {
        localStorage.setItem('auth_access_token', accessToken);
        localStorage.setItem('auth_refresh_token', accessToken); // Also store as refresh token for compatibility
        console.log('Stored access token in localStorage:', accessToken.substring(0, 10) + '...');
      }
    } catch (e) {
      console.error('Failed to store auth recovery URL:', e);
    }
    
    // Redirect to reset password page if not already there
    const isOnResetPage = window.location.pathname.includes('/reset-password');
    
    if (!isOnResetPage) {
      console.log('Redirecting to reset password page from handlePasswordResetRedirect');
      
      // 直接リダイレクト
      window.location.href = '/reset-password?type=recovery';
      return true;
    } else {
      console.log('Already on reset password page, no redirect needed');
    }
  }
  
  return false;
};

// Function to extract access token from URL
export const extractAccessTokenFromUrl = () => {
  try {
    // Try to get from search params first (for browser router)
    if (window.location.search) {
      const searchParams = new URLSearchParams(window.location.search);
      const searchToken = searchParams.get('access_token');
      
      if (searchToken) {
        console.log('Found access token in search params');
        return searchToken;
      }
    }
    
    // Try to get from hash if not found in search params
    if (window.location.hash) {
      // Check if hash contains access_token directly
      const hashParams = window.location.hash.substring(1);
      const hashSearchParams = new URLSearchParams(hashParams);
      const hashToken = hashSearchParams.get('access_token');
      
      if (hashToken) {
        console.log('Found access token in hash params');
        return hashToken;
      }
      
      // Try regex on hash if URLSearchParams didn't work
      const hashMatch = window.location.hash.match(/access_token=([^&]+)/);
      if (hashMatch && hashMatch[1]) {
        console.log('Found access token in hash with regex');
        return hashMatch[1];
      }
    }
    
    // Try to extract from full URL as last resort
    const fullUrl = window.location.href;
    const tokenMatch = fullUrl.match(/access_token=([^&]+)/);
    if (tokenMatch && tokenMatch[1]) {
      console.log('Found access token in full URL with regex');
      return tokenMatch[1];
    }
    
    // Check localStorage as final fallback
    const storedToken = localStorage.getItem('auth_access_token');
    if (storedToken) {
      console.log('Found access token in localStorage');
      return storedToken;
    }
  } catch (err) {
    console.error('Error extracting access token:', err);
  }
  
  console.log('No access token found in URL or localStorage');
  return null;
};

// Function to process password reset token
export const processPasswordResetToken = async () => {
  try {
    const fullUrl = window.location.href;
    console.log('Processing URL for token:', fullUrl);
    
    // First try to get token from URL or localStorage
    const token = extractAccessTokenFromUrl();
    if (token) {
      console.log('Access token found, setting session');
      
      // Manually set the session with the token
      const { data, error } = await supabase.auth.setSession({
        access_token: token,
        refresh_token: token, // Use same token as refresh token for compatibility
      });
      
      if (error) {
        console.error('Error setting session with token:', error);
      } else if (data?.session) {
        console.log('Successfully set session with token');
        return { success: true, session: data.session };
      }
    }
    
    // Try fallback method with getSession
    console.log('Trying fallback getSession method');
    const { data, error } = await supabase.auth.getSession();
    
    if (error) {
      console.error('Fallback method error:', error);
    } else if (data?.session) {
      console.log('Fallback method succeeded');
      return { success: true, session: data.session };
    }
    
    // Try to get existing session as last resort
    const { data: sessionData } = await supabase.auth.getSession();
    if (sessionData?.session) {
      console.log('Using existing session');
      return { success: true, session: sessionData.session };
    }
    
    console.error('All token processing methods failed');
    return { success: false, error: 'Failed to process token' };
  } catch (err) {
    console.error('Error processing reset token:', err);
    return { success: false, error: err };
  }
};

// Function to manually set session from stored token
export const setSessionFromStoredToken = async () => {
  try {
    const token = localStorage.getItem('auth_access_token');
    if (!token) {
      console.log('No stored token found');
      return { success: false };
    }
    
    console.log('Setting session from stored token');
    const { data, error } = await supabase.auth.setSession({
      access_token: token,
      refresh_token: token, // Use same token as refresh token for compatibility
    });
    
    if (error) {
      console.error('Error setting session from stored token:', error);
      return { success: false, error };
    }
    
    if (data?.session) {
      console.log('Successfully set session from stored token');
      return { success: true, session: data.session };
    }
    
    return { success: false };
  } catch (err) {
    console.error('Error setting session from stored token:', err);
    return { success: false, error: err };
  }
};

// Function to completely delete a user account with exponential backoff
export const deleteUserAccount = async (email: string) => {
  const maxRetries = 7; // Increased from 5 to 7
  const baseDelay = 2000; // Increased from 1000 to 2000ms
  let lastError = null;

  const wait = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      console.log(`Attempting to delete user account (attempt ${attempt + 1}/${maxRetries}): ${email}`);
      
      // First attempt to delete
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/delete-user`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify({ 
          email,
          attempt: attempt + 1,
          maxRetries,
          baseDelay 
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete user account');
      }

      const result = await response.json();
      
      if (result.success) {
        console.log('Delete operation reported success, verifying...');
        
        // Calculate exponential backoff delay
        const delay = baseDelay * Math.pow(2, attempt);
        await wait(delay);

        // Verify deletion
        const verifyResponse = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/check-user-exists`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
          },
          body: JSON.stringify({ email })
        });

        if (!verifyResponse.ok) {
          throw new Error('Failed to verify user deletion');
        }

        const verifyData = await verifyResponse.json();
        
        if (!verifyData.exists) {
          console.log('User deletion verified successfully');
          return { success: true, message: 'User account deleted successfully' };
        }

        console.log(`User still exists after attempt ${attempt + 1}, retrying...`);
        lastError = new Error('User still exists after deletion attempt');
        
      } else {
        lastError = new Error(result.error || 'Delete operation failed');
      }
      
      // Wait longer before next attempt
      const delay = baseDelay * Math.pow(2, attempt);
      await wait(delay);
      
    } catch (error) {
      console.error(`Error during deletion attempt ${attempt + 1}:`, error);
      lastError = error;
      
      if (attempt === maxRetries - 1) {
        throw error;
      }
      
      // Wait before next attempt
      const delay = baseDelay * Math.pow(2, attempt);
      await wait(delay);
    }
  }

  throw lastError || new Error('Failed to delete user account after multiple attempts');
};

// Function to check if a user exists and delete them if needed
export const checkAndDeleteUser = async (email: string) => {
  try {
    // First check if user exists
    const checkResponse = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/check-user-exists`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
      },
      body: JSON.stringify({ email })
    });
    
    if (!checkResponse.ok) {
      throw new Error('Failed to check if user exists');
    }
    
    const checkData = await checkResponse.json();
    
    // If user doesn't exist, no need to delete
    if (!checkData.exists) {
      return { success: true, message: 'User does not exist, no need to delete' };
    }
    
    // User exists, try to delete with retries
    return await deleteUserAccount(email);
    
  } catch (error) {
    console.error('Error in check and delete:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'ユーザーの確認と削除に失敗しました'
    };
  }
};